import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/shared/ui/button";
import { AppSidebar } from "@/shared/layout/appSidebar";
import { JelloShotLogo } from "@/shared/layout/jelloShotLogo";

export const Layout: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const onChange = () => {
      if (media.matches) {
        setMobileMenuOpen(false);
      }
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 border-r border-border bg-card md:flex md:flex-col">
        <AppSidebar />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          data-app-header
          className="sticky top-0 z-[70] border-b border-border bg-background/90 backdrop-blur-sm md:hidden"
        >
          <div className="flex h-14 items-center gap-2 px-3">
            <Button
              variant="ghost"
              size="icon"
              data-sidebar-toggle=""
              onClick={() => setMobileMenuOpen((current) => !current)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
            <Link to="/projects" aria-label="JelloShot home">
              <JelloShotLogo className="h-7" />
            </Link>
          </div>
        </header>

        <div
          className={cn(
            "fixed inset-0 z-[60] md:hidden",
            mobileMenuOpen ? "pointer-events-auto" : "pointer-events-none",
          )}
          aria-hidden={!mobileMenuOpen}
        >
          <button
            type="button"
            tabIndex={mobileMenuOpen ? 0 : -1}
            className={cn(
              "absolute inset-0 bg-foreground/30 transition-opacity duration-300",
              mobileMenuOpen ? "opacity-100" : "opacity-0",
            )}
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          />
          <div
            ref={mobileMenuRef}
            className={cn(
              "absolute inset-y-0 left-0 flex w-60 flex-col border-r border-border bg-card pt-14 shadow-xl transition-transform duration-300 ease-out",
              mobileMenuOpen ? "translate-x-0" : "-translate-x-full",
            )}
          >
            <AppSidebar
              showLogo={false}
              onNavigate={() => setMobileMenuOpen(false)}
            />
          </div>
        </div>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
